import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Facebook, Users, Trash2 } from "lucide-react";

const STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];

export default function OpsInquiries() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/charter/inquiries").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const change = async (id, status) => { await api.patch(`/charter/inquiries/${id}`, { status }); load(); };
  const remove = async (r) => {
    if (!window.confirm(`Delete inquiry from ${r.name}?`)) return;
    try { await api.delete(`/charter/inquiries/${r.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const bySource = rows.reduce((acc, r) => { acc[r.source || "direct"] = (acc[r.source || "direct"] || 0) + 1; return acc; }, {});

  return (
    <DashboardLayout role="ops">
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="as-overline">Marketing · Charter</div>
        <h1 className="as-serif text-5xl font-light mt-2" data-testid="ops-inquiries-title">Charter Inquiries</h1>

        <div className="mt-6 grid grid-cols-3 md:grid-cols-4 gap-4">
          <Kpi icon={Users} label="Total" value={rows.length}/>
          <Kpi icon={Instagram} label="Instagram" value={bySource.instagram || 0}/>
          <Kpi icon={Facebook} label="Facebook" value={bySource.facebook || 0}/>
          <Kpi label="Direct" value={bySource.direct || 0}/>
        </div>

        <div className="mt-8 as-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] text-left">
                <th className="px-6 py-3">Name / Company</th>
                <th className="px-6 py-3">Route</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Pax</th>
                <th className="px-6 py-3">Source</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="ops-inquiries-tbody">
              {rows.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-[#756E64]">No inquiries yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-[#E5DFD2] hover:bg-[#F1EDE4]" data-testid={`inquiry-row-${r.id}`}>
                  <td className="px-6 py-4">
                    <div className="text-[#1A1D24]">{r.name}</div>
                    <div className="as-mono text-[10px] text-[#756E64] mt-0.5">{r.email}{r.company && ` · ${r.company}`}</div>
                  </td>
                  <td className="px-6 py-4"><span className="as-serif text-lg">{r.departure_airport} <span className="as-gold">→</span> {r.arrival_airport}</span></td>
                  <td className="px-6 py-4 text-sm">{r.departure_date}</td>
                  <td className="px-6 py-4 text-sm">{r.passengers}</td>
                  <td className="px-6 py-4"><div className="as-pill as-pill-muted inline-block">{r.source || "direct"}</div></td>
                  <td className="px-6 py-4">
                    <select value={r.status} onChange={e => change(r.id, e.target.value)} className="bg-white border border-[#E5DFD2] focus:border-[#A47E2A] focus:outline-none px-3 py-1.5 rounded-sm text-xs" data-testid={`inquiry-status-${r.id}`}>
                      {STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => remove(r)} className="p-1.5 text-[#756E64] hover:text-red-600 hover:bg-red-50 rounded-sm" data-testid={`inquiry-delete-${r.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="as-card p-5">
      {Icon && <Icon size={14} className="text-[#756E64] mb-3"/>}
      <div className="as-serif text-3xl font-light">{value}</div>
      <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64] mt-2">{label}</div>
    </div>
  );
}
